import { Component, ViewChild, ElementRef, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { FormatService } from '../../format.service';
import { InvestmentChartService } from '../../investment-chart.service';
import { validateRange } from '../../range-validate';
import { RdCalculatorService, RdResult, RD_CONSTRAINTS } from './rd-calculator.service';

@Component({
  selector: 'app-rd-calculator',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  providers: [InvestmentChartService],
  templateUrl: './rd-calculator.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./rd-calculator.scss']
})
export class RdCalculator implements OnDestroy {
  fmt = inject(FormatService);
  private calc = inject(RdCalculatorService);
  private charts = inject(InvestmentChartService);
  private cdr = inject(ChangeDetectorRef);


  @ViewChild('breakdownRef') breakdownRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('growthRef')    growthRef!:    ElementRef<HTMLCanvasElement>;

  readonly C = RD_CONSTRAINTS;
  readonly Math = Math;

  monthlyDeposit = 5_000;
  annualRate = 6.5;
  tenureMonths = 24;

  formErrors:    Record<string, string>  = {};
  touched:       Record<string, boolean> = {};
  showFormErrors = false;

  calculated = false;
  result:     RdResult | null = null;

  private validateForm(): boolean {
    const errors: Record<string, string> = {};
    const e1 = validateRange(this.monthlyDeposit, this.C.monthlyDeposit.min, this.C.monthlyDeposit.max, 'Monthly deposit');
    if (e1) errors['monthlyDeposit'] = e1;
    const e2 = validateRange(this.annualRate, this.C.rate.min, this.C.rate.max, 'Interest rate');
    if (e2) errors['annualRate'] = e2;
    const e3 = validateRange(this.tenureMonths, this.C.tenureMonths.min, this.C.tenureMonths.max, 'Tenure');
    if (e3) errors['tenureMonths'] = e3;
    else if (Math.round(this.tenureMonths) % 3 !== 0) errors['tenureMonths'] = 'Tenure must be a multiple of 3 months.';
    this.formErrors = errors;
    return Object.keys(errors).length === 0;
  }

  get isFormValid(): boolean { return this.validateForm(); }

  onFieldChange(field: string): void {
    this.touched[field] = true;
    this.validateForm();
    if (this.calculated) {
      this.calculated = false;
      this.charts.destroyAll();
    }
  }

  onFieldBlur(field: string): void {
    this.touched[field] = true;
    if (field === 'tenureMonths') this.tenureMonths = Math.round(this.tenureMonths || 0);
    this.validateForm();
  }

  fieldError(field: string): string | null {
    return (this.touched[field] || this.showFormErrors) && this.formErrors[field]
      ? this.formErrors[field]
      : null;
  }

  hasError(field: string): boolean { return !!this.fieldError(field); }

  calculate(): void {
    this.showFormErrors = true;
    this.touched = { monthlyDeposit: true, annualRate: true, tenureMonths: true };
    if (!this.validateForm()) return;

    this.result = this.calc.calculate(this.monthlyDeposit, this.annualRate, Math.round(this.tenureMonths));
    this.calculated = true;
    this.cdr.detectChanges();

    setTimeout(() => {
      const r = this.result!;
      this.charts.renderBreakdown(this.breakdownRef.nativeElement, [
        { label: 'Total Deposited', value: r.totalInvested, color: '#3b82f6' },
        { label: 'Interest Earned', value: r.totalInterest, color: '#10b981' }
      ], 'Maturity Value');
      this.charts.renderGrowth(
        this.growthRef.nativeElement,
        r.months.map(row => `Mo ${row.month}`),
        [
          { label: 'Deposited', data: r.months.map(row => Math.round(row.invested)), color: '#94a3b8', dashed: true },
          { label: 'Balance', data: r.months.map(row => Math.round(row.balance)), color: '#3b82f6' }
        ]
      );
    }, 60);
  }

  reset(): void {
    this.monthlyDeposit = 5_000;
    this.annualRate     = 6.5;
    this.tenureMonths   = 24;
    this.formErrors     = {};
    this.touched        = {};
    this.showFormErrors = false;
    this.calculated     = false;
    this.result         = null;
    this.charts.destroyAll();
  }

  ngOnDestroy(): void {
    this.charts.destroyAll();
  }
}
